import React, { useState } from 'react';
import { PageLayout } from '@/shared/layout';
import {
  Button,
  KPICard,
  ActionToolbar,
  DataNavigator,
  Tabs,
  Badge
} from '@/components/ui';
import { useAutomation, useAutomationLogs } from './hooks/useAutomation';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { AutomationRuleCard } from './components/AutomationRuleCard';
import { AutomationStatusLog } from './components/AutomationStatusLog';
import { RuleBuilder } from './components/RuleBuilder';
import {
  WandSparkles as MagicWand,
  Plus,
  Settings,
  PlayCircle,
  History,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Activity,
  Search,
  ChevronRight,
  MoreVertical
} from 'lucide-react';
import type { AutomationRule } from './types/automation.types';

const triggerLabels: Record<string, string> = {
  schedule_due: 'Đến hạn lịch bảo dưỡng',
  work_order_overdue: 'Lệnh công việc quá hạn',
  qa_failed: 'Kiểm tra QA không đạt',
  issue_created: 'Sự cố mới được tạo',
  inventory_low: 'Vật tư dưới mức tối thiểu',
  equipment_service_due: 'Thiết bị đến hạn bảo trì',
};

const actionLabels: Record<string, string> = {
  create_work_order: 'Tự động tạo Work Order',
  send_notification: 'Gửi thông báo đẩy',
  create_issue: 'Tạo sự cố hệ thống',
  escalate_to_manager: 'Báo cáo cấp quản lý',
  assign_team: 'Tự động giao việc',
};

export default function AutomationPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const {
    rules,
    stats,
    isLoading,
    filters,
    setFilters,
    toggleRule,
    runRuleNow,
    createRule,
    updateRule
  } = useAutomation(companyId);

  const { logs = [], isLoading: isLoadingLogs } = useAutomationLogs(companyId);

  const [activeTab, setActiveTab] = useState('rules');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | undefined>(undefined);

  const handleSaveRule = async (data: Partial<AutomationRule>) => {
    if (editingRule) {
      await updateRule({ id: editingRule.id, ...data });
    } else {
      await createRule(data);
    }
    setShowBuilder(false);
    setEditingRule(undefined);
  };

  const handleEditRule = (rule: AutomationRule) => {
    setEditingRule(rule);
    setShowBuilder(true);
  };

  return (
    <PageLayout
      title="Tự động hóa Vận hành"
      description="Thiết lập quy tắc xử lý rủi ro và điều phối công việc hiện trường"
      breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'Tự động hóa' }]}
    >
      <div className="space-y-8">
        {/* KPI Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Quy tắc đang chạy"
            value={stats?.activeRules || 0}
            icon={PlayCircle}
            variant="success"
          />
          <KPICard
            title="Lần chạy hôm nay"
            value={stats?.executionsToday || 0}
            icon={History}
            variant="primary"
          />
          <KPICard
            title="Tỉ lệ thành công"
            value={Math.round(stats?.successRate || 0)}
            icon={CheckCircle2}
            variant="info"
            suffix="%"
          />
          <KPICard
            title="Cảnh báo lỗi"
            value={stats?.criticalAlerts || 0}
            icon={AlertTriangle}
            variant="danger"
          />
        </div>

        {/* Action Toolbar with Tabs Integration */}
        <div className="flex flex-col space-y-4">
           <div className="flex items-center justify-between">
              <div className="bg-white/50 backdrop-blur-xl p-1 rounded-2xl border border-white/50 shadow-sm flex gap-1">
                <button
                  onClick={() => setActiveTab('rules')}
                  className={`px-6 py-2 text-[10px] font-black  tracking-widest rounded-xl transition-all ${
                    activeTab === 'rules'
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-white/50'
                  }`}
                >
                  Quy tắc
                </button>
                <button
                  onClick={() => setActiveTab('logs')}
                  className={`px-6 py-2 text-[10px] font-black  tracking-widest rounded-xl transition-all ${
                    activeTab === 'logs'
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-white/50'
                  }`}
                >
                  Nhật ký
                </button>
              </div>

              <div className="flex gap-2">
                 <Button variant="outline" size="md" className="rounded-2xl hidden md:flex">
                    <Activity className="w-4 h-4 mr-2" />
                    Sức khỏe hệ thống
                 </Button>
                 <AuthButton
                    module="maintenance"
                    action="create"
                    variant="primary"
                    className="rounded-2xl"
                    onClick={() => {
                      setEditingRule(undefined);
                      setShowBuilder(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Thêm quy tắc mới
                  </AuthButton>
              </div>
           </div>

           <ActionToolbar
              searchValue={filters.search}
              onSearchChange={(v) => setFilters({ ...filters, search: v })}
              searchPlaceholder="Tìm kiếm quy tắc tự động hóa..."
              filterOptions={activeTab === 'rules' ? [
                {
                  id: 'type', label: 'Trigger', type: 'select', choices: [
                    { value: 'all', label: 'Tất cả trigger' },
                    ...Object.entries(triggerLabels).map(([val, label]) => ({ value: val, label }))
                  ]
                },
                {
                  id: 'status', label: 'Trạng thái', type: 'select', choices: [
                    { value: 'all', label: 'Tất cả' },
                    { value: 'active', label: 'Đang chạy' },
                    { value: 'inactive', label: 'Tạm dừng' },
                  ]
                }
              ] : []}
              onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              hidePrimaryAction
              isLoading={isLoading || isLoadingLogs}
           />
        </div>

        {activeTab === 'rules' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
            {rules.map((rule) => (
              <AutomationRuleCard
                key={rule.id}
                rule={rule}
                onToggle={(id, isActive) => toggleRule({ id, isActive })}
                onRunNow={runRuleNow}
                onEdit={handleEditRule}
                triggerLabels={triggerLabels}
                actionLabels={actionLabels}
              />
            ))}

            <button
              onClick={() => {
                setEditingRule(undefined);
                setShowBuilder(true);
              }}
              className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-100 rounded-[40px] bg-slate-50/30 hover:bg-blue-50/30 hover:border-blue-200 transition-all duration-500 group min-h-[320px]"
            >
              <div className="w-16 h-16 rounded-[24px] bg-white shadow-sm flex items-center justify-center text-slate-300 group-hover:scale-110 group-hover:text-blue-500 transition-all duration-500">
                <Plus className="w-8 h-8" />
              </div>
              <span className="mt-4 text-[10px] font-black text-slate-400 group-hover:text-blue-600  tracking-widest transition-all">Thiết lập quy tắc mới</span>
            </button>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="bg-white/30 backdrop-blur-2xl rounded-[32px] border border-white p-2 shadow-xl shadow-slate-200/50 min-h-[400px] animate-in fade-in slide-in-from-bottom-2 duration-500">
            <AutomationStatusLog logs={logs} isLoading={isLoadingLogs} />
          </div>
        )}
      </div>

      <RuleBuilder
        isOpen={showBuilder}
        onClose={() => setShowBuilder(false)}
        onSave={handleSaveRule}
        initialData={editingRule}
        triggerLabels={triggerLabels}
        actionLabels={actionLabels}
      />
    </PageLayout>
  );
}
